import { FormikErrors, FormikProps, useFormik } from 'formik';
import React, { useEffect } from 'react';
import {
    Dialog,
    Grid,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    Radio,
    Chip,
    NumberField
} from 'src/components';
import DialogContent from 'src/components/DialogContent';
import DialogTitle from 'src/components/DialogTitle';
import DialogActions from 'src/components/DialogActions';
import { translate } from 'src/i18n/tHelper';
import VwHastaUyarilisteDTO from 'src/models/HastaUyariModels/VwHastaUyarilisteDTO';
import { useAppDispatch, useAppSelector } from 'src/store';
import { HastaKaraListeSlice } from 'src/store/Slices/HastaUyariListesi/HastaKaraListeSlice';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GnlParametreSelect from 'src/parts/Genel/gnl.parametre.select';
import ParametreGrupEnum from 'src/enums/ParametreGrupEnum';
import BaseComponent from 'src/core/base.component';
import { helper } from 'src/utils/helper';
import { useGetOnlyBySearchTextMutation } from 'src/services/Kimlik/KimlikApi';
import KimlikSlice from 'src/store/Slices/Genel/KimlikSlice';
import { useHastaKaraListeEkleMutation, useHastaKaraListeGuncelleMutation, useLazyHastaKaraListeKayitSorgulaQuery } from 'src/services/HastaUyariListesi/HastaKaraListeApi';

const translatePath = "pages|hastaUyariListesi";
let formik: FormikProps<VwHastaUyarilisteDTO> = null;
const HastaKaraListesiEkle = (): JSX.Element => {
    const state = useAppSelector(s => s.hastaKaraListe);
    const kimlikState = useAppSelector(s => s.kimlik);
    const dispatch = useAppDispatch();

    const [query, queryState] = useLazyHastaKaraListeKayitSorgulaQuery();
    const [kimlikSearch, kimlikSearchState] = useGetOnlyBySearchTextMutation();

    const [ekle, ekleState] = useHastaKaraListeEkleMutation();
    const [guncelle, guncelleState] = useHastaKaraListeGuncelleMutation();

    const handleClose = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        dispatch(HastaKaraListeSlice.actions.setEkleOpen(false));
    }

    formik = useFormik({
        enableReinitialize: true,
        initialValues: state.activeRow,
        validateOnChange: false,
        onSubmit: (values) => {
            if (values?.id > 0)
                guncelle(values).unwrap().then((v) => {
                    dispatch(HastaKaraListeSlice.actions.setData({
                        ...state.data,
                        isLoading: true,
                    }));
                    handleClose(null);
                }).catch(e => e);
            else
                ekle(values).unwrap().then((v) => {
                    dispatch(HastaKaraListeSlice.actions.setData({
                        ...state.data,
                        isLoading: true,
                    }));
                    handleClose(null);
                }).catch(e => e);
        },
        validate: (values) => {
            const errors: FormikErrors<VwHastaUyarilisteDTO> = {};
            if (!values.fkGrup)
                errors.fkGrup = translate(translatePath, 'Grup seçimi zorunludur');
            if (!values.fkIslem)
                errors.fkIslem = translate(translatePath, 'İşlem türü seçimi zorunludur');
            const bosTc = values.tckimlikno && String(values.tckimlikno).trim();
            const bosPass = values.pasaportno && String(values.pasaportno).trim();
            const bosDosyaNo = values.kimlikId && String(values.kimlikId).trim();
            if (!bosTc && !bosPass && !bosDosyaNo) {
                const errorMessage = translate(translatePath, 'TC Kimlik No veya Pasaport No veya Dosya No zorunludur');
                errors.tckimlikno = errorMessage;
                errors.pasaportno = errorMessage;
                errors.kimlikId = errorMessage;
            }
            return errors;
        },
    });

    const handleSave = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (!ekleState.isLoading && !guncelleState.isLoading && !queryState.isFetching)
            formik.submitForm();
    }

    const handlerAdSoyadBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>): void => {
        if (!formik.values.ad && !formik.values.soyad)
            return;
        if (!formik.values.ad.trim() || !formik.values.soyad.trim())
            return;
        var text = helper.upperCase(formik.values.ad.trim()) + " " + helper.upperCase(formik.values.soyad.trim());
        kimlikSearch(text)
            .unwrap()
            .then(res => {
                if (res?.length > 0)
                    dispatch(KimlikSlice.actions.setState({
                        ...kimlikState,
                        kayitliKimlikler: res.filter(f => helper.UpperCaseAsc(f.adi) === helper.UpperCaseAsc(formik.values.ad) && helper.UpperCaseAsc(f.soyadi) === helper.UpperCaseAsc(formik.values.soyad)),
                        kayitliKimliklerOpen: true,
                        cancelPathName: ""
                    }))
            })
            .catch((e) => e);
    };

    useEffect(() => {
        if (!kimlikSearchState.isLoading)
            if (!kimlikState.kayitliKimliklerOpen && kimlikState.cancelPathName === "") {
                if (kimlikState.activeRow?.id > 0) {
                    formik.setFieldValue("kimlikId", kimlikState.activeRow.id);
                    formik.setFieldValue("tckimlikno", kimlikState.activeRow.kimlikNo);
                    formik.setFieldValue("pasaportno", kimlikState.activeRow.pasaportNo);
                    formik.setFieldValue("ad", kimlikState.activeRow.adi);
                    formik.setFieldValue("soyad", kimlikState.activeRow.soyadi);
                    formik.setFieldValue("babaad", kimlikState.activeRow.babaAdi);
                }
            }
    }, [kimlikState.kayitliKimliklerOpen]);

    return (
        <Dialog fullWidth open={state.ekleOpen} maxWidth="md">
            <BaseComponent loading={ekleState.isLoading || guncelleState.isLoading || queryState.isFetching || kimlikSearchState.isLoading}>
                <DialogTitle icons={<WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />}>
                    {translate(translatePath, "Hasta Uyarı Detayları")}
                </DialogTitle>
                <DialogContent sx={{ pt: 1, pb: 1 }}>
                    <Box sx={{ mb: 2 }}>
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    label={translate(translatePath, "Kayıt No")}
                                    name="id"
                                    value={formik.values.id || translate(translatePath, "Yeni Kayıt")}
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'grey.100',
                                            fontWeight: 600
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                                    <Chip
                                        icon={formik.values.aktif === 'E' ? <CheckCircleIcon /> : <CancelIcon />}
                                        label={formik.values.aktif === 'E' ? translate(translatePath, 'Uyarı Aktif') : translate(translatePath, 'Uyarı Pasif')}
                                        color={formik.values.aktif === 'E' ? 'success' : 'default'}
                                        variant={formik.values.aktif === 'E' ? 'filled' : 'outlined'}
                                        onClick={() => formik.setFieldValue('aktif', formik.values.aktif === 'E' ? 'H' : 'E')}
                                        sx={{ cursor: 'pointer', fontWeight: 500, minWidth: '120px' }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1.5, border: '1px solid', borderColor: 'primary.200' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600, display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                                <WarningIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                                {translate(translatePath, "Uyarı Bilgileri")}
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    <GnlParametreSelect
                                        required={true}
                                        parametreType={ParametreGrupEnum.HastaUyariGrubu}
                                        label={translate(translatePath, "Grubu")}
                                        name="fkGrup"
                                        value={formik.values.fkGrup}
                                        onChange={formik.handleChange}
                                        error={Boolean(formik.errors.fkGrup)}
                                        helperText={formik.errors.fkGrup}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Box
                                            onClick={() => formik.setFieldValue('fkIslem', 1)}
                                            sx={{
                                                borderRadius: 1,
                                                border: '2px solid',
                                                borderColor: formik.values.fkIslem === 1 ? 'warning.main' : 'grey.300',
                                                backgroundColor: formik.values.fkIslem === 1 ? 'warning.50' : 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.2,
                                                flex: 1,
                                                justifyContent: 'center',
                                                minHeight: '32px',
                                                '&:hover': {
                                                    borderColor: 'warning.main',
                                                    backgroundColor: 'warning.50'
                                                }
                                            }}
                                        >
                                            <Radio
                                                value={1}
                                                checked={formik.values.fkIslem === 1}
                                                size="small"
                                                sx={{
                                                    color: 'warning.main',
                                                    p: 0,
                                                    '&.Mui-checked': { color: 'warning.main' }
                                                }}
                                                onChange={formik.handleChange}
                                            />
                                            <WarningIcon fontSize="small" sx={{ color: formik.values.fkIslem === 1 ? 'warning.main' : 'grey.500' }} />
                                            <Typography variant="caption" fontWeight={formik.values.fkIslem === 1 ? 600 : 400} sx={{ color: formik.values.fkIslem === 1 ? 'warning.dark' : 'text.primary', fontSize: '0.65rem' }}>
                                                {translate(translatePath, "Kullanıcıyı Uyar")}
                                            </Typography>
                                        </Box>

                                        <Box
                                            onClick={() => formik.setFieldValue('fkIslem', 2)}
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 1,
                                                border: '2px solid',
                                                borderColor: formik.values.fkIslem === 2 ? 'error.main' : 'grey.300',
                                                backgroundColor: formik.values.fkIslem === 2 ? 'error.50' : 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.2,
                                                flex: 1,
                                                justifyContent: 'center',
                                                minHeight: '32px',
                                                '&:hover': {
                                                    borderColor: 'error.main',
                                                    backgroundColor: 'error.50'
                                                }
                                            }}
                                        >
                                            <Radio
                                                value={2}
                                                checked={formik.values.fkIslem === 2}
                                                size="small"
                                                sx={{
                                                    color: 'error.main',
                                                    p: 0,
                                                    '&.Mui-checked': { color: 'error.main' }
                                                }}
                                            />
                                            <BlockIcon
                                                fontSize="small"
                                                sx={{
                                                    color: formik.values.fkIslem === 2 ? 'error.main' : 'grey.500'
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                fontWeight={formik.values.fkIslem === 2 ? 600 : 400}
                                                sx={{
                                                    color: formik.values.fkIslem === 2 ? 'error.dark' : 'text.primary',
                                                    fontSize: '0.65rem'
                                                }}
                                            >
                                                {translate(translatePath, "Kayda İzin Verme")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {formik.errors.fkIslem && formik.touched.fkIslem && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {formik.errors.fkIslem}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                            <Box sx={{ p: 1.5, backgroundColor: 'info.50', borderRadius: 1.5, border: '1px solid', borderColor: 'info.200' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'info.main', fontWeight: 600, display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                                    <PersonIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                                    {translate(translatePath, "Kişi Bilgileri")}
                                </Typography>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={4}>
                                        <NumberField
                                            label={translate(translatePath, "Dosya No")}
                                            name="kimlikId"
                                            value={formik.values.kimlikId}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                            error={Boolean(formik.errors.kimlikId)}
                                            helperText={formik.errors.kimlikId}
                                            sx={{ backgroundColor: 'white' }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={() => {
                                                            if (!formik.values.kimlikId)
                                                                return;
                                                            query({ kayitAdi: helper.objectToString(formik.values.kimlikId) }).unwrap().then((v) => {
                                                                if (v.uyariBilgi3?.length > 0) {
                                                                    formik.setFieldValue("tckimlikno", v.uyariBilgi1);
                                                                    formik.setFieldValue("pasaportno", v.uyariBilgi2);
                                                                    formik.setFieldValue("ad", v.uyariBilgi3);
                                                                    formik.setFieldValue("soyad", v.uyariBilgi4);
                                                                    formik.setFieldValue("babaad", v.uyariBilgi5);
                                                                }
                                                            }).catch(e => e);
                                                        }}
                                                        size="small"
                                                        edge="end"
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <SearchIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label={translate(translatePath, "TC Kimlik No")}
                                            name="tckimlikno"
                                            value={formik.values.tckimlikno}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            error={Boolean(formik.errors.tckimlikno)}
                                            helperText={formik.errors.tckimlikno}
                                            sx={{ backgroundColor: 'white' }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={() => {
                                                            if (!formik.values.tckimlikno)
                                                                return;
                                                            query({ kayitAdi: helper.objectToString(formik.values.tckimlikno) }).unwrap().then((v) => {
                                                                if (v.uyariBilgi3?.length > 0) {
                                                                    formik.setFieldValue("pasaportno", v.uyariBilgi2);
                                                                    formik.setFieldValue("ad", v.uyariBilgi3);
                                                                    formik.setFieldValue("soyad", v.uyariBilgi4);
                                                                    formik.setFieldValue("babaad", v.uyariBilgi5);
                                                                    formik.setFieldValue("kimlikId", v.uyariBilgi6);
                                                                }
                                                            }).catch(e => e);
                                                        }}
                                                        size="small"
                                                        edge="end"
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <SearchIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label={translate(translatePath, "Pasaport No")}
                                            name="pasaportno"
                                            value={formik.values.pasaportno}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                            size="small"
                                            error={Boolean(formik.errors.pasaportno)}
                                            helperText={formik.errors.pasaportno}
                                            sx={{ backgroundColor: 'white' }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={() => {
                                                            if (!formik.values.pasaportno)
                                                                return;
                                                            query({ kayitAdi: formik.values.pasaportno }).unwrap().then((v) => {
                                                                if (v.uyariBilgi3?.length > 0) {
                                                                    formik.setFieldValue("tckimlikno", v.uyariBilgi1);
                                                                    formik.setFieldValue("ad", v.uyariBilgi3);
                                                                    formik.setFieldValue("soyad", v.uyariBilgi4);
                                                                    formik.setFieldValue("babaad", v.uyariBilgi5);
                                                                }
                                                            }).catch(e => e);
                                                        }}
                                                        size="small"
                                                        edge="end"
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <SearchIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label={translate(translatePath, "Adı")}
                                            name="ad"
                                            value={formik.values.ad}
                                            onChange={formik.handleChange}
                                            onBlur={handlerAdSoyadBlur}
                                            variant="outlined"
                                            size="small"
                                            error={Boolean(formik.errors.ad && formik.touched.ad)}
                                            helperText={formik.errors.ad && formik.touched.ad ? formik.errors.ad : ''}
                                            sx={{ backgroundColor: 'white' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label={translate(translatePath, "Soyadı")}
                                            name="soyad"
                                            value={formik.values.soyad}
                                            onChange={formik.handleChange}
                                            onBlur={handlerAdSoyadBlur}
                                            variant="outlined"
                                            size="small"
                                            error={Boolean(formik.errors.soyad && formik.touched.soyad)}
                                            helperText={formik.errors.soyad && formik.touched.soyad ? formik.errors.soyad : ''}
                                            sx={{ backgroundColor: 'white' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label={translate(translatePath, "Baba Adı")}
                                            name="babaad"
                                            value={formik.values.babaad}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                            size="small"
                                            sx={{ backgroundColor: 'white' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={translate(translatePath, "Açıklama")}
                                            name="aciklama"
                                            value={formik.values.aciklama}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            rows={5}
                                            error={Boolean(formik.errors.aciklama && formik.touched.aciklama)}
                                            helperText={formik.errors.aciklama && formik.touched.aciklama ? formik.errors.aciklama : ''}
                                            placeholder={translate(translatePath, "Uyarı ile ilgili detaylı açıklama giriniz...")}
                                            sx={{ backgroundColor: 'white' }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button customType="close" onClick={handleClose} />
                    <Button customType="save" loading={ekleState.isLoading || guncelleState.isLoading} disabled={!formik.dirty} onClick={handleSave} />
                </DialogActions>
            </BaseComponent>
        </Dialog>
    );
};

export default HastaKaraListesiEkle;